import { useEffect, useMemo, useState } from "react";
import AppShell from "../components/layout/AppShell";
import Spinner from "../components/common/Spinner";
import ColumnChart from "../components/common/ColumnChart";
import Card, { Empty } from "../components/numbers/Card";
import WeekendGrid from "../components/numbers/WeekendGrid";
import ProfitSection from "../components/numbers/ProfitSection";
import { getInsights } from "../api/insights";
import { formatMoney as formatUSD } from "../utils/money";

// How the business is doing, forward-looking first: what's on the books, how
// full the weekends are, what each event earns. "Collected" only ever counts
// money that has actually landed.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function ym(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** The last `n` calendar months ending with this one, oldest first. */
function lastMonths(n) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = n - 1; i >= 0; i -= 1) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(m);
  }
  return out;
}

const usd0 = (cents) =>
  `$${Math.round(Number(cents || 0) / 100).toLocaleString()}`;

function compactUsd(cents) {
  const v = Number(cents || 0) / 100;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `$${Math.round(v)}`;
}

const pct = (num, den) => (den > 0 ? `${Math.round((num / den) * 100)}%` : "—");

export default function AdminNumbers() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getInsights()
      .then(setData)
      .catch((e) => setError(e.message || "Could not load your numbers."));
  }, []);

  const view = useMemo(() => {
    if (!data) return null;
    const collected = Object.fromEntries(
      (data.collected_by_month || []).map((r) => [r.month, Number(r.cents || 0)])
    );
    const months = lastMonths(12);
    const series = months.map((m) => ({
      key: ym(m),
      label: MONTHS[m.getMonth()],
      long: `${MONTHS[m.getMonth()]} ${m.getFullYear()}`,
      value: collected[ym(m)] || 0,
    }));
    const thisMonth = ym(new Date());
    const yearTotal = series.reduce((s, d) => s + d.value, 0);
    const f = data.funnel || {};
    const ob = data.on_books || {};
    return {
      series,
      yearTotal,
      collectedThisMonth: collected[thisMonth] || 0,
      ob,
      f,
      avgJob: f.booked > 0 ? Number(f.booked_value) / Number(f.booked) : 0,
    };
  }, [data]);

  return (
    <AppShell title="Numbers">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}
      {!data && !error ? (
        <div className="py-16 flex justify-center">
          <Spinner label="Crunching..." />
        </div>
      ) : view ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-2.5">
            <Stat label="Collected this month" value={usd0(view.collectedThisMonth)} />
            <Stat
              label="On the books"
              value={usd0(view.ob.value)}
              hint={`${view.ob.jobs || 0} event${Number(view.ob.jobs) === 1 ? "" : "s"} in the next 90 days`}
            />
            <Stat
              label="Still to collect"
              value={usd0(view.f.balances_owed)}
              hint="balances on booked jobs"
            />
            <Stat
              label="Open quotes"
              value={usd0(view.f.awaiting_value)}
              hint={`${view.f.awaiting || 0} waiting on payment`}
            />
          </div>

          <ComingUp ob={view.ob} />

          <WeekendGrid />

          <ProfitSection />

          <Card
            title="Collected by month"
            subtitle={`${usd0(view.yearTotal)} over the last 12 months`}
          >
            <ColumnChart
              title="Money collected by month, last 12 months"
              data={view.series}
              format={(c) => formatUSD(c)}
              compact={compactUsd}
            />
          </Card>

          <div className="grid grid-cols-3 gap-2.5">
            <Stat small label="Quote → booked" value={pct(Number(view.f.booked), Number(view.f.sent))} />
            <Stat small label="Average job" value={view.avgJob ? usd0(view.avgJob) : "—"} />
            <Stat
              small
              label="Leads this month"
              value={data.lead_totals ? String(data.lead_totals.leads_this_month) : "—"}
            />
          </div>

          <Card
            title="Where paying jobs come from"
            subtitle="Each lead is followed through to whether it booked"
          >
            {data.lead_sources?.length ? (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-birdie-charcoal/45">
                      <th className="text-left font-semibold py-1.5 px-1">Channel</th>
                      <th className="text-right font-semibold py-1.5 px-1">Leads</th>
                      <th className="text-right font-semibold py-1.5 px-1">Booked</th>
                      <th className="text-right font-semibold py-1.5 px-1">Rate</th>
                      <th className="text-right font-semibold py-1.5 px-1">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lead_sources.map((s) => (
                      <tr key={s.channel} className="border-t border-birdie-green/10">
                        <td className="py-2 px-1 text-birdie-charcoal font-medium">{s.channel}</td>
                        <td className="py-2 px-1 text-right tabular-nums text-birdie-charcoal/75">{s.leads}</td>
                        <td className="py-2 px-1 text-right tabular-nums text-birdie-charcoal/75">{s.booked}</td>
                        <td className="py-2 px-1 text-right tabular-nums text-birdie-charcoal/75">
                          {pct(Number(s.booked), Number(s.leads))}
                        </td>
                        <td className="py-2 px-1 text-right tabular-nums text-birdie-charcoal font-semibold">
                          {usd0(s.booked_value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty>
                Starts filling in as leads arrive. Website enquiries are tracked automatically; log
                phone and text leads from the Inbox so they count too.
              </Empty>
            )}
          </Card>

          <Card title="Why jobs don't book" subtitle="From quotes and leads you marked lost">
            <PriceSignal ps={data.price_signal} />
            {data.lost_reasons?.length ? (
              <ul className="divide-y divide-birdie-green/10">
                {data.lost_reasons.map((r) => (
                  <li key={r.reason} className="flex justify-between py-2 text-sm">
                    <span className="text-birdie-charcoal">{r.reason}</span>
                    <span className="tabular-nums text-birdie-charcoal/60">{r.n}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>Nothing marked lost yet.</Empty>
            )}
          </Card>
        </div>
      ) : null}
    </AppShell>
  );
}

function Stat({ label, value, hint, small }) {
  return (
    <div className="bg-white border border-birdie-green/12 rounded-xl px-3 py-2.5">
      <div className="text-[11px] text-birdie-charcoal/55">{label}</div>
      <div className={`font-semibold text-birdie-charcoal mt-0.5 ${small ? "text-base" : "text-xl"}`}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-birdie-charcoal/40 mt-0.5">{hint}</div>}
    </div>
  );
}

/** The next three months of booked work, plus quotes still open for those dates. */
function ComingUp({ ob }) {
  const months = ob.by_month || [];
  const pipe = ob.pipeline || {};
  return (
    <Card
      title="Coming up"
      subtitle={`${usd0(ob.collected)} already collected · ${usd0(ob.to_collect)} due before the events`}
    >
      {months.length ? (
        <ul className="divide-y divide-birdie-green/10">
          {months.map((m) => {
            const [y, mo] = m.month.split("-").map(Number);
            return (
              <li key={m.month} className="flex justify-between py-2 text-sm">
                <span className="text-birdie-charcoal">
                  {MONTHS[mo - 1]} {y}
                  <span className="text-birdie-charcoal/45 text-xs">
                    {" "}
                    · {m.jobs} event{Number(m.jobs) === 1 ? "" : "s"}
                  </span>
                </span>
                <span className="tabular-nums font-semibold text-birdie-charcoal">{usd0(m.value)}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <Empty>Nothing booked in the next 90 days yet.</Empty>
      )}
      {Number(pipe.quotes) > 0 && (
        <p className="mt-2 text-xs text-birdie-charcoal/55">
          Plus {usd0(pipe.value)} in {pipe.quotes} open quote{Number(pipe.quotes) === 1 ? "" : "s"} for
          these dates if they pay.
        </p>
      )}
    </Card>
  );
}

/** "Too expensive" losses vs what actually books — is there a price ceiling? */
function PriceSignal({ ps }) {
  if (!ps || !ps.lost_n) return null;
  const higher = ps.booked_n > 0 && ps.lost_median > ps.booked_median;
  return (
    <div className="mb-3 rounded-xl bg-birdie-cream/70 px-3 py-2 text-xs text-birdie-charcoal/70">
      Lost on price: median quote <strong className="text-birdie-charcoal">{usd0(ps.lost_median)}</strong>{" "}
      ({ps.lost_n} quote{ps.lost_n === 1 ? "" : "s"})
      {ps.booked_n > 0 && (
        <>
          {" "}
          vs <strong className="text-birdie-charcoal">{usd0(ps.booked_median)}</strong> for jobs that booked
        </>
      )}
      .
      {higher && " Bigger quotes are the ones getting turned down — consider a smaller package option."}
    </div>
  );
}
